// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

// about vue-router
// MIT License
// 
// Copyright (c) 2013-present Evan You
// 
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
// 
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
// 
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.

import { mapActions, useStore } from 'vuex';
import { ref, computed, getCurrentInstance, onBeforeUnmount } from 'vue';
import { onBeforeRouteLeave, } from 'vue-router';

import processingView from '../../../../share/components/vue/ProcessingIcon';
import fullModal from "../../../../share/components/vue/FullModal";
import { ICON_KEY, BUTTON_TYPE, default as noTextSvgButton } from "../../../../share/components/vue/NoTextSVGButton";
import wrappedTeleport from "../../../../share/components/vue/WrappedTeleport";
import { WrappedUseI18n } from '../../../../share/plugins/I18nWrapper/index';
import dataMonitor from '../../../vue/individualWorkSupport/dataMonitor/DataMonitorWs146.vue';
import dmUtil from "../../dataMonitor/dataMonitorUtil";
import { exportCsv } from './dataMonitor/csv';
import apiService from '../../../../share/util/api/service';
import handler from '../../../../share/util/apiResultValidator';
import { getCsvFileName } from '../../../../util/fileExport';

export default {
  components: {
    processingView,
    dataMonitor,
    fullModal,
    noTextSvgButton,
    wrappedTeleport
  },

  setup(props, context) {
    const currentComponent = getCurrentInstance();

    // 他ファイルで$t, $teを使えるように追加
    const { t, te } = WrappedUseI18n();
    currentComponent.$t = t;
    currentComponent.$te = te;

    // 他ファイルで$modalDialogを使えるように追加 ※share/alert.jsのshowXXX()を使用するために使用
    currentComponent.$modalDialog = currentComponent.appContext.config.globalProperties.$modalDialog;

    // 子コンポーネントの参照
    const data_monitor = ref(null);
    // 親コンポーネントからのデータ
    // リアクティブな変数
    const processing = ref(false);
    const inPlaying = ref(false);
    const checkLines = ref(0);  // グラフ項目件数
    const currentPercentage = ref(0);
    const dataMonitorFunctionSettings = new dmUtil.DataMonitorFunctionSettings(dmUtil.TYPE_CALLER.PLAY_WS146); // DM機能の設定インスタンス
    // 全画面モーダルの表示有無
    const showModal = ref(false);
    const showSelect = ref(false);
    // Measureコンポーネントの描画完了したか否か。Measureコンポーネントへのteleportを描画完了まで待つ必要があるため、チェックする。
    const measureMounted = ref(false);
    // VuexのStoreの値・特定の算出の値
    const store = useStore();
    const cachedDetails = computed(() => store.state.cache.projectDetails).value;
    const btnDisabled = computed(() => !checkLines.value); // ボタン使用不可制御
    // 描画に使用せずコンポーネント内で保持するのみの値
    let measurementId = '';

    /**
     * WS.146のデータモニタの再生画面を表示するためのデータを取得する
     * @param {object} projectId プロジェクトID
     * @param {object} measuredId 計測ID
     * @param {object} selectEcuName 選択したシステム名
     * @param {string} requirementId 再生対象のrequirementId
     * @param {object} self コンポーネントのインスタンス(this) ※handler.validateの関数内で使用するために引数で受け取る
     */
    async function createWorkSupport146MeasuresList(projectId, measuredId, selectEcuName, requirementId, self) {
      measurementId = measuredId;
      try {

        processing.value = true;
        const workSupportEndPoint = '/workSupports/' + projectId + '/' + measuredId;
        const params = { region_info: self.cachedProjectDataSource };
        // 計測データ取得
        const resWorkSupport = await apiService.getParamCall(apiService.WSP, workSupportEndPoint, params, null);

        const isSuccess = handler.validate(handler.validateTypes.all, resWorkSupport, self, null, async () => { },
          (result) => {
            if (result.behaviorType === handler.behaviorTypes.logout) self.logout(self);
          });
        if (!isSuccess) {
          return;
        }
        processing.value = false;
        const measuredData = JSON.parse(JSON.stringify(resWorkSupport));
        const currentMeasuredId = data_monitor.value.measuredId;

        if (currentMeasuredId === measuredId) {
          // 言語変更時にデータの再取得を実施しない
          return;
        }

        data_monitor.value.startPlayApi(
          measuredId,
          measuredData.data.project.file_path,
          measuredData.data.project.total_number_of_files,
          measuredData.data.project.start_time,
          selectEcuName,
          projectId,
          requirementId
        );
        // 計測データ取得と同時に、モーダル表示
        showModal.value = true;
      } finally {
        processing.value = false;
      }
    }

    // グラフ設定
    function handleConfigureGraph() {
      data_monitor.value.handleConfigureGraph();
    }

    /**
     * メモボタン押下
     * DataRecollection.jsのopenMemoModalを呼び出している
     */
    async function parentOpenMemoModal() {
      context.emit('openMemoModal');
    }

    /** 計測開始/停止状態切替 */
    function togglePlaying(flg) {
      if (flg === undefined || flg === null) {
        inPlaying.value = !inPlaying.value;
      } else {
        inPlaying.value = flg;
      }
    }

    /** グラフ項目件数を変更 */
    function changeCheckValue(value) {
      checkLines.value = value;
    }

    /**
     * 戻るボタン押下時
     * DataRecollection.jsのbackToDetailsを呼び出している
     */
    function backSelect() {
      context.emit('backToDetails');
    }

    /**
     * 取得したデータポイントの更新時の処理。プログレスバーの取得率を更新する。
     * @param {number} gotTotalCount 取得済みのデータポイント数
     */
    function updateTotalCount(gotTotalCount) {
      if (!data_monitor.value || !inPlaying) {
        return;
      }
      const allCount = data_monitor.value.receivedDataPoints;
      if (gotTotalCount) {
        const percentage = Math.trunc(gotTotalCount / allCount * 100);
        if (percentage >= 100) {
          currentPercentage.value = 100;
        } else {
          currentPercentage.value = percentage;
        }
      }
    }

    /**
     * ファイルエクスポートボタン押下時の処理
     */
    async function csvExport() {
      processing.value = true;
      let measuredDataName = '';
      if (cachedDetails?.measures) {
        // 計測データ名を取得
        const targetMeasurement = cachedDetails.measures[0][apiService.WSP].find(measure => String(measure.registered_at) === measurementId);
        measuredDataName = targetMeasurement ? targetMeasurement.name : '';
      }
      // CSVファイル名を取得
      const fileName = getCsvFileName(
        currentComponent.$t('individual.individual_diagnosis_function_work_support'),
        measuredDataName,
        cachedDetails.information.vin || ''
      );
      try {
        await exportCsv(currentComponent, data_monitor, cachedDetails, measurementId, fileName, () => backSelect());
      } finally {
        processing.value = false;
      }
    }

    /**
     * 計測データ取得処理の停止
     */
    async function stopFetchingMeasurement() {
      if (inPlaying.value) {
        // 直前の再生データ取得中であれば停止する
        await data_monitor.value.stopFetchingMeasurement();
        inPlaying.value = false;
        currentPercentage.value = 0;
      }
    }

    /**
     * 最大化ボタン押下処理
     */
    function maximum() {
      showModal.value = true;
    }

    /**
     * 最小化ボタン押下処理
     */
    function minimum() {
      showModal.value = false;
    }

    /**
     * 計測画面全体をteleportさせるモーダルダイアログのIDを作成。再生画面でのID被りを避けるため、画面ごとの値を設定
     * @returns {string} 作成したID
     */
    function modalContentId() {
      return `full-modal-content-${dataMonitorFunctionSettings.typeCaller}`;
    }

    /**
     * 計測画面全体をteleportさせるモーダルダイアログのIDを作成。再生画面でのID被りを避けるため、画面ごとの値を設定
     * @returns {string} 作成したID
     */
    function graphSettingsDestinationId() {
      return `graph-settings-destination-${dataMonitorFunctionSettings.typeCaller}`;
    }

    /**
     * 子コンポーネントからDataMonitorMeasureのMount・Unmountの通知を受け取る
     * @param {boolean} isMounted Mountされたか否か
     */
    function measureMountedEvent(isMounted) {
      measureMounted.value = isMounted;
      if (!isMounted) {
        // 計測画面のコンポーネントが破棄されたら、計測画面を離れたと判断して全画面モーダルも閉じる
        showModal.value = false;
      }
    }

    async function debugCsvExport() {
      const isDebug = process.env.VUE_APP_DEBUG_LOG && process.env.VUE_APP_DEBUG_LOG === "true";
      if (!isDebug) {
        return;
      }

      processing.value = true;
      const fileNameId = 'debugWorkSupport';

      const originT = currentComponent.$t;
      const originTe = currentComponent.$te;

      // 意図的に翻訳できないようにする
      currentComponent.$t = (v) => v;
      currentComponent.$te = () => false;
      new dmUtil.Cache().destroy();

      try {
        await exportCsv(currentComponent, data_monitor, cachedDetails, measurementId, fileNameId, () => backSelect(), true);
      } finally {
        processing.value = false;
        // 戻す
        currentComponent.$t = originT;
        currentComponent.$te = originTe;
      }

      const conversionList = data_monitor.value.conversionList;
      dmUtil.jsonFileExport(conversionList, 'debugConversionList.json');

      const didConversionMap = data_monitor.value.didConversionMap;
      dmUtil.jsonFileExport(didConversionMap, 'debugDidConversionMap.json');
    }

    onBeforeUnmount(async () => {
      new dmUtil.Cache().destroy();
      // 画面遷移前に再生データ取得中であれば停止する
      await stopFetchingMeasurement();
    });

    onBeforeRouteLeave((to, from, next) => {
      showSelect.value = true;
      next();
    });

    return {
      ...mapActions('auth', ['logout']),
      ICON_KEY,
      BUTTON_TYPE,
      data_monitor,
      processing,
      inPlaying,
      btnDisabled,
      currentPercentage,
      dataMonitorFunctionSettings,
      showModal,
      measureMounted,
      showSelect,
      createWorkSupport146MeasuresList,
      handleConfigureGraph,
      parentOpenMemoModal,
      togglePlaying,
      changeCheckValue,
      backSelect,
      csvExport,
      updateTotalCount,
      stopFetchingMeasurement,
      maximum,
      minimum,
      modalContentId,
      graphSettingsDestinationId,
      measureMountedEvent,
      debugCsvExport
    };
  }
};